import { useEffect, useState, useRef } from "react";
import { ref, onValue, set, update, get } from "firebase/database";
import { db } from "../firebase";
import { 
  FaCircle, FaPlus, FaEllipsisV, FaSearch, FaThumbtack, FaCheckDouble, 
  FaChevronDown, FaUser, FaTimes, FaBullseye, FaUserPlus, FaCommentMedical, 
  FaCheck, FaSyncAlt, FaUserCircle, FaRobot
} from "react-icons/fa";

function ChatList({ currentUser, onSelectUser, selectedUser }) {
  const [users, setUsers] = useState([]);
  const [chats, setChats] = useState({});
  const [statuses, setStatuses] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showAnnouncement, setShowAnnouncement] = useState(true);

  // Menu Dropdown & Modal States
  const [showMenu, setShowMenu] = useState(false);
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalTab, setModalTab] = useState("contacts"); // "contacts" | "add_contact"
  const [contactSearch, setContactSearch] = useState("");
  const [newContactName, setNewContactName] = useState("");
  const [newContactEmail, setNewContactEmail] = useState("");
  const [newContactBio, setNewContactBio] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const menuRef = useRef(null);

  /* Close menu on click outside */
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setShowMenu(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ===============================
     LOAD USERS
  =============================== */
  useEffect(() => {
    const usersRef = ref(db, "users");

    onValue(usersRef, (snapshot) => {
      const data = snapshot.val() || {};

      const selfData = data[currentUser.uid] || {};
      const selfUser = {
        uid: currentUser.uid,
        name: `${selfData.name || currentUser.displayName || "You"} (You)`,
        photoURL: selfData.photoURL || currentUser.photoURL || "",
        bio: "Message yourself • Save notes & links",
        isSelf: true
      };

      const otherUsers = Object.entries(data)
        .filter(([uid]) => uid !== currentUser.uid)
        .map(([uid, value]) => ({
          uid,
          ...value
        }));

      setUsers([selfUser, ...otherUsers]);
    });
  }, [currentUser.uid, currentUser.displayName, currentUser.photoURL]);

  /* ===============================
     LOAD CHAT PREVIEWS
  =============================== */
  useEffect(() => {
    const chatsRef = ref(db, "chats");

    onValue(chatsRef, (snapshot) => {
      setChats(snapshot.val() || {});
    });
  }, []);

  /* ===============================
     LOAD ONLINE STATUS
  =============================== */
  useEffect(() => {
    const statusRef = ref(db, "status");

    onValue(statusRef, (snapshot) => {
      setStatuses(snapshot.val() || {});
    });
  }, []);

  const getChatId = (otherUid) => {
    return currentUser.uid < otherUid
      ? `${currentUser.uid}_${otherUid}`
      : `${otherUid}_${currentUser.uid}`;
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  /* Clean raw markdown from chat list preview */
  const cleanPreviewText = (text, isSelf = false) => {
    if (!text) return isSelf ? "Message yourself" : "Start chat";
    return text
      .replace(/\*\*(.*?)\*\*/g, "$1")
      .replace(/\*(.*?)\*/g, "$1")
      .replace(/`(.*?)`/g, "$1")
      .replace(/#+\s?/g, "")
      .replace(/\[(.*?)\]\(.*?\)/g, "$1");
  };

  let totalUnreadCount = 0;
  users.forEach((user) => {
    const chatId = getChatId(user.uid);
    const chat = chats?.[chatId];
    totalUnreadCount += chat?.participants?.[currentUser.uid]?.unreadCount || 0;
  });

  /* Filter & Search Logic */
  const filteredUsers = users.filter((user) => {
    const chatId = getChatId(user.uid);
    const chat = chats?.[chatId];
    const unread = chat?.participants?.[currentUser.uid]?.unreadCount || 0;

    const nameMatch = (user.name || "User").toLowerCase().includes(searchQuery.toLowerCase());
    const msgMatch = (chat?.lastMessage || "").toLowerCase().includes(searchQuery.toLowerCase());

    const matchesSearch = nameMatch || msgMatch;

    if (!matchesSearch) return false;

    if (activeFilter === "unread") {
      return unread > 0;
    }
    if (activeFilter === "favourites") {
      return user.isFavourite || chat?.isFavourite;
    }
    if (activeFilter === "pinned") {
      return chat?.isPinned || user.isPinned;
    }
    return true;
  });

  /* Modal Contact Search Filter */
  const modalFilteredUsers = users.filter((user) => 
    (user.name || "User").toLowerCase().includes(contactSearch.toLowerCase()) ||
    (user.email || "").toLowerCase().includes(contactSearch.toLowerCase())
  );

  /* Action: Add New Contact to Firebase */
  const handleAddNewContact = async (e) => {
    e.preventDefault();
    if (!newContactName.trim()) {
      alert("Please enter a contact name.");
      return;
    }
    setIsSubmitting(true);

    try {
      // Create unique UID for contact
      const contactUid = `contact_${Date.now()}`;
      const newContact = {
        uid: contactUid,
        name: newContactName.trim(),
        email: newContactEmail.trim() || `${newContactName.toLowerCase().replace(/\s+/g, '')}@bharatconnect.com`,
        bio: newContactBio.trim() || "Hey there! I am using Bharat Connect.",
        photoURL: "",
        createdAt: Date.now()
      };

      await set(ref(db, `users/${contactUid}`), newContact);

      // Reset form & select user
      setNewContactName("");
      setNewContactEmail("");
      setNewContactBio("");
      setShowModal(false);
      onSelectUser(newContact);
    } catch (err) {
      console.error("Error adding contact:", err);
      alert("Failed to add contact: " + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  /* Action: Mark All as Read */
  const handleMarkAllRead = async () => {
    setShowMenu(false);
    try {
      const updates = {};
      users.forEach((user) => {
        const chatId = getChatId(user.uid);
        if (chats?.[chatId]?.participants?.[currentUser.uid]) {
          updates[`chats/${chatId}/participants/${currentUser.uid}/unreadCount`] = 0;
        }
      });
      if (Object.keys(updates).length > 0) {
        await update(ref(db), updates);
      }
      alert("All chats marked as read!");
    } catch (err) {
      console.error("Error marking all read:", err);
    }
  };

  /* ===============================
     IN-MEMORY DATA REFRESH (NO PAGE RELOAD)
  =============================== */
  const handleRefresh = async () => {
    setShowMenu(false);
    setIsRefreshing(true);
    try {
      const [usersSnap, chatsSnap, statusSnap] = await Promise.all([
        get(ref(db, "users")),
        get(ref(db, "chats")),
        get(ref(db, "status"))
      ]);

      if (usersSnap.exists()) {
        const data = usersSnap.val() || {};
        const selfData = data[currentUser.uid] || {};
        const selfUser = {
          uid: currentUser.uid,
          name: `${selfData.name || currentUser.displayName || "You"} (You)`,
          photoURL: selfData.photoURL || currentUser.photoURL || "",
          bio: "Message yourself • Save notes & links",
          isSelf: true
        };
        const otherUsers = Object.entries(data)
          .filter(([uid]) => uid !== currentUser.uid)
          .map(([uid, value]) => ({ uid, ...value }));
        setUsers([selfUser, ...otherUsers]);
      }

      if (chatsSnap.exists()) {
        setChats(chatsSnap.val() || {});
      }

      if (statusSnap.exists()) {
        setStatuses(statusSnap.val() || {});
      }
    } catch (err) {
      console.error("Refresh error:", err);
    } finally {
      setTimeout(() => setIsRefreshing(false), 400);
    }
  };

  return (
    <>
      <div 
        className="sidebar-header"
        style={{
          height: '60px',
          minHeight: '60px',
          maxHeight: '60px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '0 16px',
          background: 'var(--chat-list-bg)',
          boxSizing: 'border-box',
          flexShrink: 0
        }}
      >
        <h2>Chats</h2>
        <div className="sidebar-header-actions" ref={menuRef}>
          <FaPlus 
            title="New Chat / Add Contact" 
            className="header-action-btn"
            onClick={() => {
              setModalTab("contacts");
              setShowModal(true);
            }} 
          />
          <FaEllipsisV 
            title="Menu" 
            className="header-action-btn"
            onClick={() => setShowMenu(!showMenu)} 
          />

          {/* Header Dropdown Menu */}
          {showMenu && (
            <div className="header-menu-dropdown">
              <div className="menu-item" onClick={() => { setShowMenu(false); setModalTab("contacts"); setShowModal(true); }}>
                <FaCommentMedical className="menu-item-icon" />
                <span>New Chat</span>
              </div>
              <div className="menu-item" onClick={() => { setShowMenu(false); setModalTab("add_contact"); setShowModal(true); }}>
                <FaUserPlus className="menu-item-icon" />
                <span>Add Contact</span>
              </div>
              <div className="menu-item" onClick={handleMarkAllRead}>
                <FaCheck className="menu-item-icon" />
                <span>Mark all as read</span>
              </div>
              <div className="menu-item" onClick={handleRefresh}>
                <FaSyncAlt className={`menu-item-icon ${isRefreshing ? "spin" : ""}`} />
                <span>{isRefreshing ? "Syncing..." : "Refresh"}</span>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="sidebar-search-container" style={{ flexShrink: 0 }}>
        <div className="search-input-box">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search or start a new chat"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <FaTimes
              className="search-clear-icon"
              onClick={() => setSearchQuery("")}
              title="Clear search"
            />
          )}
        </div>
      </div>
      
      <div className="filter-pills-container" style={{ flexShrink: 0 }}>
        <span
          className={`filter-pill ${activeFilter === "all" ? "active" : ""}`}
          onClick={() => setActiveFilter("all")}
        >
          All
        </span>
        <span
          className={`filter-pill ${activeFilter === "unread" ? "active" : ""}`}
          onClick={() => setActiveFilter("unread")}
        >
          Unread {totalUnreadCount > 0 ? `(${totalUnreadCount})` : ""}
        </span>
        <span
          className={`filter-pill ${activeFilter === "favourites" ? "active" : ""}`}
          onClick={() => setActiveFilter("favourites")}
        >
          Favourites
        </span>
        <span 
          className={`filter-pill filter-dropdown ${showFilterDropdown ? "active" : ""}`}
          onClick={() => setShowFilterDropdown(!showFilterDropdown)}
          title="More Filters"
          style={{ cursor: 'pointer' }}
        >
          <FaChevronDown style={{ transform: showFilterDropdown ? 'rotate(180deg)' : 'none', transition: 'transform 0.2s ease' }} />
        </span>
      </div>

      {/* FILTER DROPDOWN MENU (OUTSIDE OVERFLOW CONTAINER TO PREVENT CLIPPING) */}
      {showFilterDropdown && (
        <div style={{ position: 'relative', width: '100%', flexShrink: 0, zIndex: 1000 }}>
          <div 
            className="header-menu-dropdown"
            style={{
              position: 'absolute',
              top: '2px',
              right: '14px',
              zIndex: 1000,
              minWidth: '180px',
              background: 'var(--panel-header-bg)',
              border: '1px solid var(--border-light)',
              borderRadius: '12px',
              padding: '6px 0',
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.6)'
            }}
          >
            <div 
              className="menu-item" 
              onClick={() => { setActiveFilter("all"); setShowFilterDropdown(false); }}
              style={{ padding: '10px 16px', cursor: 'pointer', color: activeFilter === 'all' ? '#00a884' : 'var(--text-primary)', fontWeight: activeFilter === 'all' ? '600' : 'normal' }}
            >
              <span>All Chats</span>
            </div>
            <div 
              className="menu-item" 
              onClick={() => { setActiveFilter("unread"); setShowFilterDropdown(false); }}
              style={{ padding: '10px 16px', cursor: 'pointer', color: activeFilter === 'unread' ? '#00a884' : 'var(--text-primary)', fontWeight: activeFilter === 'unread' ? '600' : 'normal' }}
            >
              <span>Unread</span>
            </div>
            <div 
              className="menu-item" 
              onClick={() => { setActiveFilter("favourites"); setShowFilterDropdown(false); }}
              style={{ padding: '10px 16px', cursor: 'pointer', color: activeFilter === 'favourites' ? '#00a884' : 'var(--text-primary)', fontWeight: activeFilter === 'favourites' ? '600' : 'normal' }}
            >
              <span>Favourites</span>
            </div>
            <div 
              className="menu-item" 
              onClick={() => { setActiveFilter("pinned"); setShowFilterDropdown(false); }}
              style={{ padding: '10px 16px', cursor: 'pointer', color: activeFilter === 'pinned' ? '#00a884' : 'var(--text-primary)', fontWeight: activeFilter === 'pinned' ? '600' : 'normal' }}
            >
              <span>Pinned Chats</span>
            </div>
          </div>
        </div>
      )}

      {/* ANNOUNCEMENT BANNER */}
      {showAnnouncement && (
        <div 
          className="announcement-banner"
          style={{
            background: 'linear-gradient(90deg, rgba(0, 168, 132, 0.15) 0%, rgba(0, 168, 132, 0.05) 100%)',
            borderBottom: '1px solid rgba(0, 168, 132, 0.2)',
            padding: '8px 14px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            fontSize: '12px',
            fontWeight: '500',
            color: '#00a884',
            flexShrink: 0
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <FaRobot style={{ fontSize: '14px', color: '#00a884' }} />
            <span><strong>Bharat Connect v3.0</strong> • Powered by Sampark AI</span>
          </div>
          <FaTimes 
            style={{ cursor: 'pointer', opacity: 0.7, fontSize: '13px' }} 
            onClick={() => setShowAnnouncement(false)} 
            title="Dismiss"
          />
        </div>
      )}

      <div className="chat-item-list" style={{ flex: 1, height: 0, minHeight: 0, overflowY: 'auto', overflowX: 'hidden' }}>
        {filteredUsers.length === 0 ? (
          <div className="no-chats-found">
            <FaUser className="no-chats-icon" />
            <p>No chats found</p>
            <span>Try searching for another contact or add a new contact above.</span>
            <button 
              className="add-contact-shortcut-btn"
              onClick={() => { setModalTab("add_contact"); setShowModal(true); }}
            >
              <FaUserPlus /> Add New Contact
            </button>
          </div>
        ) : (
          filteredUsers.map((user) => {
            const chatId = getChatId(user.uid);
            const chat = chats?.[chatId];
            const unreadCount = chat?.participants?.[currentUser.uid]?.unreadCount || 0;
            const isOnline = statuses?.[user.uid]?.state === "online";
            const isPinned = chat?.participants?.[currentUser.uid]?.isPinned;

            return (
              <div
                key={user.uid}
                className={`chat-item ${selectedUser?.uid === user.uid ? "active" : ""}`}
                onClick={() => onSelectUser(user)}
              >
                <div className="avatar">
                  {user.photoURL ? (
                    <img src={user.photoURL} alt={user.name} />
                  ) : (
                    <div className="avatar-placeholder">
                      {(user.name || "U")[0].toUpperCase()}
                    </div>
                  )}
                  {isOnline && <div className="online-indicator" />}
                </div>

                <div className="chat-info">
                  <div className="chat-header-row">
                    <span className="name">{user.name || "User"}</span>
                    <span className="time">{formatTime(chat?.updatedAt)}</span>
                  </div>

                  <div className="chat-message-row">
                    <span className="last-message">
                      {chat?.lastSenderId === currentUser.uid && (
                        <FaCheckDouble className="list-read-receipt" />
                      )}
                      {cleanPreviewText(chat?.lastMessage, user.isSelf)}
                    </span>

                    <div className="chat-badges">
                      {isPinned && <FaThumbtack className="pin-icon" title="Pinned" />}
                      {unreadCount > 0 && <div className="badge">{unreadCount}</div>}
                    </div>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* NEW CHAT & ADD CONTACT MODAL */}
      {showModal && (
        <div className="community-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="community-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modalTab === "contacts" ? "Start New Chat" : "Add New Contact"}</h3>
              <FaTimes className="close-modal-icon" onClick={() => setShowModal(false)} />
            </div>

            <div className="modal-tabs">
              <button 
                className={`modal-tab-btn ${modalTab === "contacts" ? "active" : ""}`}
                onClick={() => setModalTab("contacts")}
              >
                <FaUser /> Select Contact ({users.length})
              </button>
              <button 
                className={`modal-tab-btn ${modalTab === "add_contact" ? "active" : ""}`}
                onClick={() => setModalTab("add_contact")}
              >
                <FaUserPlus /> Add Contact
              </button>
            </div>

            {modalTab === "contacts" ? (
              <div className="modal-contacts-body">
                <div className="sidebar-search-container" style={{ padding: "0 0 14px 0" }}>
                  <div className="search-input-box">
                    <FaSearch className="search-icon" />
                    <input
                      type="text"
                      placeholder="Search contacts..."
                      value={contactSearch}
                      onChange={(e) => setContactSearch(e.target.value)}
                    />
                  </div>
                </div>

                <div className="modal-contact-list">
                  {modalFilteredUsers.length === 0 ? (
                    <div className="no-chats-found" style={{ padding: "20px 0" }}>
                      <p>No matching contacts</p>
                      <button 
                        className="add-contact-shortcut-btn"
                        onClick={() => setModalTab("add_contact")}
                      >
                        <FaUserPlus /> Add New Contact
                      </button>
                    </div>
                  ) : (
                    modalFilteredUsers.map((user) => (
                      <div
                        key={user.uid}
                        className="contact-picker-item"
                        onClick={() => {
                          onSelectUser(user);
                          setShowModal(false);
                        }}
                      >
                        <div className="avatar">
                          {user.photoURL ? (
                            <img src={user.photoURL} alt={user.name} />
                          ) : (
                            <div className="avatar-placeholder">
                              {(user.name || "U")[0].toUpperCase()}
                            </div>
                          )}
                          {statuses?.[user.uid]?.state === "online" && <div className="online-indicator" />}
                        </div>
                        <div className="contact-picker-info">
                          <h4>{user.name || "User"}</h4>
                          <p>{user.bio || user.email || "Available"}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            ) : (
              <form onSubmit={handleAddNewContact} className="modal-add-contact-form">
                <div className="input-group">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="e.g. Ramesh Kumar"
                    value={newContactName}
                    onChange={(e) => setNewContactName(e.target.value)}
                  />
                </div>

                <div className="input-group">
                  <label>Email or Phone</label>
                  <input
                    type="text"
                    placeholder="e.g. ramesh@gmail.com or +919876543210"
                    value={newContactEmail}
                    onChange={(e) => setNewContactEmail(e.target.value)}
                  />
                </div>

                <div className="input-group">
                  <label>About / Status</label>
                  <input
                    type="text"
                    placeholder="e.g. Available for chat"
                    value={newContactBio}
                    onChange={(e) => setNewContactBio(e.target.value)}
                  />
                </div>

                <div className="modal-actions">
                  <button 
                    type="button" 
                    className="cancel-btn" 
                    onClick={() => setShowModal(false)}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="create-submit-btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? "Adding..." : "Add & Open Chat"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </>
  );
}

export default ChatList;
